import React from "react";
import { cn } from "../../../lib/cn";
import { Box } from "../BoxReset";

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {SystemStyleObject['alignItems']} [props.align] - align-items property
 * @param {SystemStyleObject['justifyContent']} [props.justify] - justify-content property
 * @param {SystemStyleObject['flexDirection']} [props.direction] - flex-direction property
 * @param {SystemStyleObject['flexWrap']} [props.wrap] - flex-wrap property
 * @param {SystemStyleObject['flexBasis']} [props.basis] - flex-basis property
 * @param {SystemStyleObject['flexGrow']} [props.grow] - flex-grow property
 * @param {SystemStyleObject['flexShrink']} [props.shrink] - flex-shrink property
 * @param {boolean} [props.inline=false] - Whether to use inline-flex
 * @param {string} [props.className] - Additional classes
 * @param {0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 16 | 18 | 20 | 22} [props.gap] - gap property
 * @returns {JSX.Element}
 */
const Flex = ({ children, align, justify, direction = "row", wrap, basis, grow, shrink, inline = false, className, gap, ...props }) => {
  const classes = cn(
    inline ? "inline-flex" : "flex",
    {
      "items-start": align === "flex-start",
      "items-end": align === "flex-end",
      "items-center": align === "center",
      "items-baseline": align === "baseline",
      "items-stretch": align === "stretch",

      "justify-start": justify === "flex-start",
      "justify-end": justify === "flex-end",
      "justify-center": justify === "center",
      "justify-between": justify === "space-between",
      "justify-around": justify === "space-around",
      "justify-evenly": justify === "space-evenly",

      "flex-row": direction === "row",
      "flex-row-reverse": direction === "row-reverse",
      "flex-col": direction === "column",
      "flex-col-reverse": direction === "column-reverse",

      "flex-wrap": wrap === "wrap",
      "flex-wrap-reverse": wrap === "wrap-reverse",
      "flex-nowrap": wrap === "nowrap",

      [`basis-${basis}`]: basis,

      "grow-0": grow === 0,
      grow: grow === 1,

      "shrink-0": shrink === 0,
      shrink: shrink === 1,

      "gap-0": gap === 0,
      "gap-1": gap === 1,
      "gap-2": gap === 2,
      "gap-3": gap === 3,
      "gap-4": gap === 4,
      "gap-5": gap === 5,
      "gap-6": gap === 6,
      "gap-7": gap === 7,
      "gap-8": gap === 8,
      "gap-9": gap === 9,
      "gap-10": gap === 10,
      "gap-11": gap === 11,
      "gap-12": gap === 12,
      "gap-14": gap === 14,
      "gap-16": gap === 16,
      "gap-18": gap === 18,
      "gap-20": gap === 20,
    },
    className
  );

  return (
    <Box
      className={classes}
      style={{
        gap: gap > 20 ? gap * 4 : undefined,
        grow: grow > 1 ? grow : undefined,
        shrink: shrink > 1 ? shrink : undefined,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export default Flex;
